import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion'
import {
  activeSubscriptionSelector,
  loggedInSelector,
} from 'spa/selectors/users'
import { DynamicMessage } from '../../components/message'
import {
  faqMessages,
  featuresMessages,
  messages,
  pricingMsg,
  tableMessages,
} from './localizedMessages'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedHTMLMessage, injectIntl } from 'react-intl'
import { Helmet } from 'react-helmet'
import { openModal, SIGNUP_MODAL } from 'spa/action_creators/modals'
import { TextStyle } from 'spa/components/typography'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import styles from './styles'

const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Why is DJcity the Best Download Record Pool for DJs?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Founded in 2000, DJcity is one of the longest-running and most trusted record pools, providing DJs worldwide with the latest club and radio hits in high-quality MP3 format.',
      },
    },
    {
      '@type': 'Question',
      name: 'What Makes DJcity Better Than Other Record Pools?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'DJcity offers unmatched value with 250+ genres, exclusive remixes and edits, and flexible subscription plans with major savings.',
      },
    },
    {
      '@type': 'Question',
      name: 'Who Uses DJcity?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'DJcity is an essential music source for beginner, club, radio, and expert DJs. Artists and performers who rely on DJcity include: Diplo, Tiesto, DJ Snake, Lil Jon, Vice, Walshy Fire (Major Lazer), Grandmaster Flash, James Hype, A-Trak, Joe Jonas, and Q-Tip.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where Can I Find DJcity Reviews?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'DJcity is highly rated and endorsed by top DJ industry platforms, including Serato, Digital DJ Tips, Phase DJ, Crossfader, and DJ Techtools.',
      },
    },
  ],
}

const Feature = injectIntl(({ intl, msgs, href }) => {
  return (
    <div className={styles.feature}>
      <a
        className={styles.displayContents}
        href={href}
        rel="noreferrer"
        target="_blank"
      >
        <h2 aria-level={2} className={styles.title}>
          <FormattedHTMLMessage {...msgs.title} />
        </h2>
      </a>
      <div className={styles.featureDjc}>
        <span className={styles.name}>
          {intl.formatMessage(featuresMessages[6].djcity)}&nbsp;
        </span>
        <span className={styles.content}>
          {intl.formatMessage(msgs.djcity)}
        </span>
      </div>
      <div className={styles.featureComp}>
        <span className={styles.name}>
          {intl.formatMessage(featuresMessages[6].competitors)}&nbsp;
        </span>
        <span className={styles.content}>
          {intl.formatMessage(msgs.competitors)}
        </span>
      </div>
    </div>
  )
})

export const PricingSection = injectIntl(({ intl }) => {
  const isLoggedIn = useSelector(loggedInSelector)
  const isActive = useSelector(activeSubscriptionSelector)
  const dispatch = useDispatch()

  const getUrl = () => {
    if (isLoggedIn) {
      if (!isActive) return '/payment-details'
      else return '/home'
    }
  }

  const handleClick = () => {
    if (!isLoggedIn) dispatch(openModal(SIGNUP_MODAL))
  }

  return (
    <section className={styles.pricing}>
      <h2>{intl.formatMessage(pricingMsg[0].title)}</h2>
      <div className={styles.pricingTable}>
        <a href={getUrl()} rel="noreferrer" target="_blank">
          <div className={styles.monthly} onClick={handleClick}>
            <div className={styles.monthlyContent}>
              <span className={styles.duration}>
                {intl.formatMessage(pricingMsg[1].duration)}
              </span>
              <span className={styles.price}>
                <FormattedHTMLMessage {...pricingMsg[1].price} />
              </span>
              <small className={styles.ppm}>
                {intl.formatMessage(pricingMsg[1].ppm)}
              </small>
            </div>
            <div className={styles.monthlyFooter} />
          </div>
        </a>
        <a href={getUrl()} rel="noreferrer" target="_blank">
          <div className={styles.biannual} onClick={handleClick}>
            <div className={styles.biannualTitle}>
              {intl.formatMessage(pricingMsg[2].header)}
            </div>
            <div className={styles.biannualContent}>
              <span className={styles.duration}>
                {intl.formatMessage(pricingMsg[2].duration)}
              </span>
              <span className={styles.price}>
                <FormattedHTMLMessage {...pricingMsg[2].price} />
              </span>
              <small className={styles.ppm}>
                {intl.formatMessage(pricingMsg[2].ppm)}
              </small>
            </div>
            <div className={styles.biannualFooter}>
              <span className={styles.saving}>
                {intl.formatMessage(pricingMsg[2].saving)}
              </span>
            </div>
          </div>
        </a>
      </div>
    </section>
  )
})

export const ComparisonTableSection = injectIntl(({ intl, className }) => {
  return (
    <section className={classNames(styles.comparison, className)}>
      <table>
        <thead>
          <th>{intl.formatMessage(tableMessages.header.col1)}</th>
          <th>{intl.formatMessage(tableMessages.header.col2)}</th>
          <th>{intl.formatMessage(tableMessages.header.col3)}</th>
          <th>{intl.formatMessage(tableMessages.header.col4)}</th>
        </thead>
        <tbody>
          {Object.entries(tableMessages)
            .slice(1)
            .map(([k, v]) => (
              <tr key={k}>
                {Object.entries(v).map(([key, value]) => (
                  <td key={k + '_' + key}>
                    <DynamicMessage
                      defaultMessage={value.defaultMessage}
                      id={value.id}
                    />
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </section>
  )
})

const Comparison = ({ intl }) => {
  const isLoggedIn = useSelector(loggedInSelector)
  const isActive = useSelector(activeSubscriptionSelector)
  const dispatch = useDispatch()

  const [isOpen, setOpen] = useState(null)

  const getUrl = () => {
    if (isLoggedIn) {
      if (!isActive) return '/payment-details'
      else return '/home'
    }
  }

  const handleClick = () => {
    if (!isLoggedIn) dispatch(openModal(SIGNUP_MODAL))
  }

  const handleFaq = ([uuid]) =>
    uuid === isOpen ? setOpen(null) : setOpen(uuid)

  return (
    <main className={styles.page}>
      <Helmet
        defer={false}
        htmlAttributes={{
          itemScope: undefined,
          itemType: 'https://schema.org/FAQPage',
        }}
      >
        <title key="pagetitle">
          {`${intl.formatMessage(messages.pageTitle)} | DJcity`}
        </title>
        <meta
          content={intl.formatMessage(messages.pageDescription)}
          name="description"
        />
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <section className={styles.header}>
        <h1 aria-level={1}>{intl.formatMessage(messages.title)}</h1>
        <TextStyle color="white">
          {intl.formatMessage(messages.subtitle)}
        </TextStyle>
      </section>

      <section className={styles.hero}>
        <Feature href="/playlists" msgs={featuresMessages[0]} />
        <a href="/playlists" target="_blank">
          <img src="/assets/cdn/comparison_header.png" />
        </a>
      </section>

      <section className={styles.subheader}>
        <Feature href="/exclusives" msgs={featuresMessages[1]} />
        <Feature href="/home" msgs={featuresMessages[2]} />
        <Feature href="/genres" msgs={featuresMessages[3]} />
        <div>
          <a href="/genres" target="_blank">
            <img
              className={styles.clickable}
              src="/assets/cdn/comparison_subheader.png"
            />
          </a>
        </div>
      </section>

      <section className={styles.subheaderAlt}>
        <Feature href="/home" msgs={featuresMessages[4]} />
        <a className={styles.displayContents} href="/home" target="_blank">
          <img
            className={styles.clickable}
            src="/assets/cdn/comparison_discovery.png"
          />
        </a>
        <Feature href="/global" msgs={featuresMessages[5]} />
        <a className={styles.displayContents} href="/global" target="_blank">
          <img
            className={styles.clickable}
            src="/assets/cdn/comparison_global.png"
          />
        </a>
      </section>

      <PricingSection />

      <section className={styles.c2a}>
        <span>{intl.formatMessage(messages.c2aTitle)}</span>
        <a href={getUrl()} rel="noreferrer" target="_blank">
          <button onClick={handleClick}>
            {intl.formatMessage(messages.c2aButton)}
          </button>
        </a>
      </section>

      <ComparisonTableSection />

      <section className={styles.faqs}>
        <h2>{intl.formatMessage(messages.faqTitle)}</h2>
        <Accordion
          // allowMultipleExpanded
          className={styles.accordion}
          onChange={handleFaq}
        >
          {faqMessages.map((faq, idx) => (
            <AccordionItem
              className={styles.accordionItem}
              dangerouslySetExpanded={isOpen === faq.uuid}
              itemprop="mainEntity"
              itemscope
              itemtype="https://schema.org/Question"
              key={'integrations_faq_' + idx}
              uuid={faq.uuid}
            >
              <AccordionItemHeading className={styles.accordionHeading}>
                <AccordionItemButton
                  className={styles.accordionButton}
                  itemprop="name"
                >
                  <DynamicMessage
                    aria-level={3}
                    defaultMessage={faq.msg.title.defaultMessage}
                    id={faq.msg.title.id}
                    tag="h3"
                  />
                  <FontAwesomeIcon
                    color={isOpen === faq.uuid ? 'red' : 'black'}
                    icon={`chevron-${isOpen === faq.uuid ? 'down' : 'right'}`}
                  />
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel
                className={styles.accordionPanel}
                itemprop="acceptedAnswer"
                itemscope
                itemtype="https://schema.org/Answer"
              >
                <DynamicMessage
                  defaultMessage={faq.msg.content.defaultMessage}
                  id={faq.msg.content.id}
                  itemprop="text"
                />
              </AccordionItemPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </main>
  )
}

Comparison.propTypes = {
  intl: PropTypes.object,
}

export default injectIntl(Comparison)
